import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PhotoIcon from '@mui/icons-material/Photo';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import CountdownTimer from '../components/CountdownTimer';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentFamily } = useFamily();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [foodDialogOpen, setFoodDialogOpen] = useState(false);
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [playlistDialogOpen, setPlaylistDialogOpen] = useState(false);
  const [songDialogOpen, setSongDialogOpen] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [foodFormData, setFoodFormData] = useState({
    name: '',
    description: '',
    quantity_needed: 1,
  });
  const [rsvpFormData, setRsvpFormData] = useState({
    status: 'yes',
    guests: 1,
    notes: '',
  });
  const [photoFormData, setPhotoFormData] = useState({
    image_url: '',
    caption: '',
  });
  const [playlistFormData, setPlaylistFormData] = useState({
    name: '',
    description: '',
  });
  const [songFormData, setSongFormData] = useState({
    title: '',
    artist: '',
    spotify_url: '',
    youtube_url: '',
    apple_music_url: '',
  });
  const [chatMessage, setChatMessage] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    host: '',
    event_date: '',
    location: '',
    event_type: 'holiday',
    image_url: '',
  });
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  const fetchEvent = useCallback(async () => {
    try {
      const response = await api.get(`/events/${id}/`);
      setEvent(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEvent();
    // Set up chat polling
    const interval = setInterval(() => {
      if (tabValue === 4) { // Chat tab
        fetchEvent();
      }
    }, 2000);
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [id, tabValue, fetchEvent]);

  const handleAddFoodItem = async () => {
    try {
      const response = await api.post(`/events/${id}/add_food_item/`, foodFormData);
      setEvent({
        ...event,
        food_items: [...event.food_items, response.data],
      });
      setFoodDialogOpen(false);
      setFoodFormData({ name: '', description: '', quantity_needed: 1 });
    } catch (error) {
      alert('Failed to add food item');
    }
  };

  const handleSignup = async (foodItemId, quantity) => {
    try {
      await api.post(`/events/${id}/food-items/${foodItemId}/signup/`, {
        quantity,
      });
      fetchEvent();
    } catch (error) {
      alert(error.response?.data?.error || error.response?.data?.detail || 'Failed to sign up');
    }
  };

  const handleCancelSignup = async (foodItemId) => {
    try {
      await api.delete(`/events/${id}/food-items/${foodItemId}/signup/`);
      fetchEvent();
    } catch (error) {
      alert(error.response?.data?.error || error.response?.data?.detail || 'Failed to cancel signup');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container>
        <Typography>Event not found</Typography>
      </Container>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const isSignedUp = (foodItem) => {
    return foodItem.signups?.some((signup) => signup.user_id === user?.uid);
  };

  const userRsvp = event?.rsvps?.find((rsvp) => rsvp.user_id === user?.uid);

  const handleRsvp = async () => {
    try {
      await api.post(`/events/${id}/rsvp/`, rsvpFormData);
      fetchEvent();
      setRsvpDialogOpen(false);
    } catch (error) {
      alert('Failed to submit RSVP');
    }
  };

  const handleAddPhoto = async () => {
    if (!currentFamily) {
      alert('Please select a family first');
      return;
    }
    try {
      await api.post(`/events/${id}/add_photo/`, photoFormData);
      fetchEvent();
      setPhotoDialogOpen(false);
      setPhotoFormData({ image_url: '', caption: '' });
    } catch (error) {
      alert('Failed to add photo');
    }
  };

  const handleSendMessage = async () => {
    if (!chatMessage.trim()) return;
    try {
      await api.post(`/events/${id}/send_message/`, { message: chatMessage });
      setChatMessage('');
      fetchEvent();
    } catch (error) {
      alert('Failed to send message');
    }
  };

  const handleCreatePlaylist = async () => {
    if (!currentFamily) {
      alert('Please select a family first');
      return;
    }
    try {
      await api.post(`/playlists/`, {
        ...playlistFormData,
        event_id: id,
        family_id: currentFamily.id,
      });
      fetchEvent();
      setPlaylistDialogOpen(false);
      setPlaylistFormData({ name: '', description: '' });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to create playlist');
    }
  };

  const handleAddSong = async () => {
    try {
      await api.post(`/playlists/${selectedPlaylist.id}/add_song/`, songFormData);
      fetchEvent();
      setSongDialogOpen(false);
      setSongFormData({
        title: '',
        artist: '',
        spotify_url: '',
        youtube_url: '',
        apple_music_url: '',
      });
    } catch (error) {
      alert('Failed to add song');
    }
  };

  const handleEditClick = () => {
    if (!event) return;
    // Format date for datetime-local input
    const eventDate = new Date(event.event_date);
    const formattedDate = new Date(eventDate.getTime() - eventDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    
    setEditFormData({
      title: event.title || '',
      description: event.description || '',
      host: event.host || '',
      event_date: formattedDate,
      location: event.location || '',
      event_type: event.event_type || 'holiday',
      image_url: event.image_url || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEvent = async () => {
    try {
      // Convert datetime-local to ISO string with timezone
      const localDate = new Date(editFormData.event_date);
      const eventDateISO = localDate.toISOString();
      
      if (editImageFile) {
        // Use FormData if there's an image file
        const formDataToSend = new FormData();
        formDataToSend.append('title', editFormData.title);
        formDataToSend.append('description', editFormData.description);
        formDataToSend.append('host', editFormData.host);
        formDataToSend.append('event_date', eventDateISO); // Use converted ISO format
        formDataToSend.append('location', editFormData.location);
        formDataToSend.append('event_type', editFormData.event_type);
        if (editFormData.image_url) {
          formDataToSend.append('image_url', editFormData.image_url);
        }
        formDataToSend.append('image', editImageFile);
        
        await api.put(`/events/${id}/`, formDataToSend);
      } else {
        // No image file, use regular JSON with converted date
        await api.put(`/events/${id}/`, {
          ...editFormData,
          event_date: eventDateISO, // Use converted ISO format
        });
      }
      
      await fetchEvent();
      setEditDialogOpen(false);
      setEditImageFile(null);
      setEditImagePreview(null);
      alert('Event updated successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update event');
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/events/${id}/`);
      alert('Event deleted successfully!');
      navigate('/events');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete event');
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container>
        <Typography>Event not found</Typography>
      </Container>
    );
  }

  // Check if user can edit/delete (creator or admin)
  const isCreator = user && event.created_by === user.uid;
  const userMember = currentFamily?.members?.find((m) => m.user_id === user?.uid);
  const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);
  const canEditOrDelete = isCreator || isAdmin;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button onClick={() => navigate('/events')} sx={{ mb: 2 }}>
        ← Back to Events
      </Button>

      <Card sx={{ mb: 3 }}>
        {(event.image_url_display || event.image_url) && (
          <CardMedia
            component="img"
            height={300}
            image={event.image_url_display || event.image_url}
            alt={event.title}
            sx={{ objectFit: 'cover' }}
          />
        )}
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {event.title}
          </Typography>
          {event.event_type && (
            <Chip
              label={event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
              sx={{ mb: 1 }}
            />
          )}
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Host: {event.host}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            📅 {formatDate(event.event_date)}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            📍 {event.location}
          </Typography>
          {event.description && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {event.description}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <CountdownTimer targetDate={event.event_date} />
          </Box>
          {canEditOrDelete && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
              >
                Edit Event
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
              >
                Delete Event
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mb: 2 }}>
        <Button
          variant={userRsvp ? 'outlined' : 'contained'}
          onClick={() => setRsvpDialogOpen(true)}
          sx={{ mr: 1 }}
        >
          {userRsvp ? `RSVP: ${userRsvp.status.toUpperCase()}` : 'RSVP'}
        </Button>
        {event.rsvp_count && (
          <Chip
            label={`${event.rsvp_count.yes} Yes, ${event.rsvp_count.maybe} Maybe, ${event.rsvp_count.no} No`}
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab icon={<EventIcon />} label="Food" />
        <Tab icon={<PhotoIcon />} label="Photos" />
        <Tab icon={<MusicNoteIcon />} label="Playlist" />
        <Tab icon={<ChatIcon />} label="Chat" />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Food Signups</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setFoodDialogOpen(true)}
            >
              Add Food Item
            </Button>
          </Box>

          {event.food_items?.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>No food items yet. Add one to get started!</Typography>
          </CardContent>
        </Card>
      ) : (
        event.food_items?.map((foodItem) => {
          const signedUp = isSignedUp(foodItem);
          const quantityBrought = foodItem.signups?.reduce(
            (sum, s) => sum + s.quantity,
            0
          ) || 0;
          const stillNeeded = foodItem.quantity_needed - quantityBrought;

          return (
            <Card key={foodItem.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Box>
                    <Typography variant="h6">{foodItem.name}</Typography>
                    {foodItem.description && (
                      <Typography variant="body2" color="text.secondary">
                        {foodItem.description}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={`${quantityBrought}/${foodItem.quantity_needed} brought`}
                        color={stillNeeded > 0 ? 'warning' : 'success'}
                        size="small"
                      />
                    </Box>
                    {foodItem.signups?.length > 0 && (
                      <List dense>
                        {foodItem.signups.map((signup) => (
                          <ListItem key={signup.id}>
                            <ListItemText
                              primary={signup.user_name}
                              secondary={`Bringing ${signup.quantity}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                  <Box>
                    {signedUp ? (
                      <Button
                        color="error"
                        onClick={() => handleCancelSignup(foodItem.id)}
                      >
                        Cancel
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => handleSignup(foodItem.id, 1)}
                        disabled={stillNeeded <= 0}
                      >
                        Sign Up
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })
          )}
        </>
      )}

      {tabValue === 1 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Photo Gallery</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPhotoDialogOpen(true)}
            >
              Add Photo
            </Button>
          </Box>
          {event.photos?.length === 0 ? (
            <Card>
              <CardContent>
                <Typography>No photos yet. Add the first one!</Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {event.photos?.map((photo) => (
                <Grid item xs={12} sm={6} md={4} key={photo.id}>
                  <Card>
                    <Box
                      component="img"
                      src={photo.image_url}
                      alt={photo.caption}
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                      }}
                    />
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        {photo.user_name}
                      </Typography>
                      {photo.caption && (
                        <Typography variant="body2">{photo.caption}</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {tabValue === 2 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Music Playlist</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPlaylistDialogOpen(true)}
            >
              Create Playlist
            </Button>
          </Box>
          {event.playlists?.length === 0 ? (
            <Card>
              <CardContent>
                <Typography>No playlists yet. Create one!</Typography>
              </CardContent>
            </Card>
          ) : (
            event.playlists?.map((playlist) => (
              <Card key={playlist.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h6">{playlist.name}</Typography>
                      {playlist.description && (
                        <Typography variant="body2" color="text.secondary">
                          {playlist.description}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedPlaylist(playlist);
                        setSongDialogOpen(true);
                      }}
                    >
                      Add Song
                    </Button>
                  </Box>
                  {playlist.songs?.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No songs yet
                    </Typography>
                  ) : (
                    <List>
                      {playlist.songs.map((song) => (
                        <ListItem key={song.id}>
                          <ListItemText
                            primary={`${song.title} - ${song.artist}`}
                            secondary={song.added_by_name}
                          />
                          {song.spotify_url && (
                            <Button
                              size="small"
                              href={song.spotify_url}
                              target="_blank"
                              sx={{ mr: 1 }}
                            >
                              Spotify
                            </Button>
                          )}
                          {song.youtube_url && (
                            <Button
                              size="small"
                              href={song.youtube_url}
                              target="_blank"
                              sx={{ mr: 1 }}
                            >
                              YouTube
                            </Button>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </>
      )}

      {tabValue === 3 && (
        <>
          <Typography variant="h5" gutterBottom>
            Event Chat
          </Typography>
          <Paper sx={{ p: 2, mb: 2, maxHeight: 400, overflow: 'auto' }}>
            {event.chat_messages?.length === 0 ? (
              <Typography>No messages yet. Start the conversation!</Typography>
            ) : (
              event.chat_messages?.map((msg) => (
                <Box key={msg.id} sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {msg.user_name} - {new Date(msg.created_at).toLocaleString()}
                  </Typography>
                  <Typography variant="body1">{msg.message}</Typography>
                </Box>
              ))
            )}
          </Paper>
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              placeholder="Type a message..."
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSendMessage();
                }
              }}
            />
            <Button variant="contained" onClick={handleSendMessage}>
              Send
            </Button>
          </Box>
        </>
      )}

      <Dialog
        open={foodDialogOpen}
        onClose={() => setFoodDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Food Item</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Food Item Name"
            margin="normal"
            value={foodFormData.name}
            onChange={(e) =>
              setFoodFormData({ ...foodFormData, name: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            value={foodFormData.description}
            onChange={(e) =>
              setFoodFormData({ ...foodFormData, description: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Quantity Needed"
            type="number"
            margin="normal"
            value={foodFormData.quantity_needed}
            onChange={(e) =>
              setFoodFormData({
                ...foodFormData,
                quantity_needed: parseInt(e.target.value) || 1,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFoodDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddFoodItem} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rsvpDialogOpen}
        onClose={() => setRsvpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>RSVP</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={rsvpFormData.status}
              onChange={(e) =>
                setRsvpFormData({ ...rsvpFormData, status: e.target.value })
              }
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="maybe">Maybe</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Number of Guests"
            type="number"
            margin="normal"
            value={rsvpFormData.guests}
            onChange={(e) =>
              setRsvpFormData({
                ...rsvpFormData,
                guests: parseInt(e.target.value) || 1,
              })
            }
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            margin="normal"
            multiline
            rows={3}
            value={rsvpFormData.notes}
            onChange={(e) =>
              setRsvpFormData({ ...rsvpFormData, notes: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRsvpDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRsvp} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Photo</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Image URL"
            margin="normal"
            value={photoFormData.image_url}
            onChange={(e) =>
              setPhotoFormData({ ...photoFormData, image_url: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Caption"
            margin="normal"
            multiline
            rows={2}
            value={photoFormData.caption}
            onChange={(e) =>
              setPhotoFormData({ ...photoFormData, caption: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddPhoto} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={playlistDialogOpen}
        onClose={() => setPlaylistDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Playlist</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Playlist Name"
            margin="normal"
            value={playlistFormData.name}
            onChange={(e) =>
              setPlaylistFormData({ ...playlistFormData, name: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={playlistFormData.description}
            onChange={(e) =>
              setPlaylistFormData({
                ...playlistFormData,
                description: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlaylistDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreatePlaylist} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={songDialogOpen}
        onClose={() => setSongDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Song</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Song Title"
            margin="normal"
            value={songFormData.title}
            onChange={(e) =>
              setSongFormData({ ...songFormData, title: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Artist"
            margin="normal"
            value={songFormData.artist}
            onChange={(e) =>
              setSongFormData({ ...songFormData, artist: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Spotify URL (optional)"
            margin="normal"
            value={songFormData.spotify_url}
            onChange={(e) =>
              setSongFormData({ ...songFormData, spotify_url: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="YouTube URL (optional)"
            margin="normal"
            value={songFormData.youtube_url}
            onChange={(e) =>
              setSongFormData({ ...songFormData, youtube_url: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Apple Music URL (optional)"
            margin="normal"
            value={songFormData.apple_music_url}
            onChange={(e) =>
              setSongFormData({
                ...songFormData,
                apple_music_url: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSongDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddSong} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Event Title"
            margin="normal"
            value={editFormData.title}
            onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
          />
          <TextField
            fullWidth
            label="Host Name"
            margin="normal"
            value={editFormData.host}
            onChange={(e) => setEditFormData({ ...editFormData, host: e.target.value })}
          />
          <TextField
            fullWidth
            label="Location"
            margin="normal"
            value={editFormData.location}
            onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
          />
          <TextField
            fullWidth
            label="Event Date & Time"
            type="datetime-local"
            margin="normal"
            value={editFormData.event_date}
            onChange={(e) => setEditFormData({ ...editFormData, event_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Event Type</InputLabel>
            <Select
              value={editFormData.event_type}
              onChange={(e) => setEditFormData({ ...editFormData, event_type: e.target.value })}
            >
              <MenuItem value="holiday">Holiday</MenuItem>
              <MenuItem value="birthday">Birthday</MenuItem>
              <MenuItem value="anniversary">Anniversary</MenuItem>
              <MenuItem value="wedding">Wedding</MenuItem>
              <MenuItem value="graduation">Graduation</MenuItem>
              <MenuItem value="party">Party</MenuItem>
              <MenuItem value="gathering">Gathering</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            value={editFormData.description}
            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
          />
          <TextField
            fullWidth
            type="file"
            accept="image/*"
            margin="normal"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setEditImageFile(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                  setEditImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
              }
            }}
            helperText="Upload a new image for the event"
            InputLabelProps={{ shrink: true }}
          />
          {editImagePreview && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img
                src={editImagePreview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
              />
            </Box>
          )}
          <TextField
            fullWidth
            label="Image URL (optional - alternative to upload)"
            margin="normal"
            value={editFormData.image_url}
            onChange={(e) => setEditFormData({ ...editFormData, image_url: e.target.value })}
            helperText="Or paste an image URL instead of uploading"
            placeholder="https://example.com/image.jpg"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateEvent} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{event?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventDetail;
